import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/features/auth/store';
import { getErrorMessage } from '@/types/api';
import type { LanguageOption, SettingsState } from '../types';

const AVAILABLE_LANGUAGES: LanguageOption[] = [
  { value: 'en', label: 'EN' },
  { value: 'he', label: 'HE' },
  { value: 'sy', label: 'SY' },
];

export const useSettings = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.actions.logout);

  const [state, setState] = useState<SettingsState>({
    userSettings: null,
    availableLanguages: AVAILABLE_LANGUAGES,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (user) {
      setState(prev => ({
        ...prev,
        userSettings: {
          userName: user.name || '',
          language: i18n.language,
          isRTL: i18n.dir() === 'rtl',
        },
      }));
    }
  }, [user, i18n.language]);

  const changeLanguage = async (languageCode: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      await i18n.changeLanguage(languageCode);

      setState(prev => ({
        ...prev,
        loading: false,
        userSettings: prev.userSettings
          ? {
              ...prev.userSettings,
              language: languageCode,
              isRTL: i18n.dir() === 'rtl',
            }
          : null,
      }));
    } catch (error) {
      const errorMessage =
        getErrorMessage(error, true) || 'Failed to change language';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const handleLogout = () => {
    logout();
    navigate({ to: '/auth/login' });
  };

  return {
    userSettings: state.userSettings,
    availableLanguages: state.availableLanguages,
    loading: state.loading,
    error: state.error,
    changeLanguage,
    handleLogout,
  };
};
